import React, { useEffect, useState } from "react";
import { ArrowRight, MapPin, Phone, Shield } from "lucide-react";
import { t } from "../../i18n/translations";
import TrustBadges from "../shared/TrustBadges";

const HeroSectionNew = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative min-h-[90vh] flex items-center overflow-hidden bg-white">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-10 w-72 h-72 bg-red-50 rounded-full blur-3xl opacity-60" />
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-teal-50 rounded-full blur-3xl opacity-40" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-slate-50 rounded-full blur-3xl opacity-30" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div
            className={`transition-opacity duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          >
            <div className="inline-flex items-center gap-2 bg-red-50 text-red-700 rounded-full px-4 py-1.5 text-sm font-semibold mb-6 border border-red-100">
              <Shield size={14} />
              {t("hero.badge")}
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-[1.1] tracking-tight mb-4">
              {t("hero.title").split(".").map((part, i) => (
                <span key={i}>
                  {part.trim()}
                  {i < 2 && <span className="text-red-600">.</span>}
                  {i < 2 && <br className="hidden sm:block" />}
                </span>
              ))}
            </h1>

            <p className="text-lg text-slate-600 leading-relaxed mb-4 max-w-xl">{t("hero.subtitle")}</p>

            <p className="text-sm font-semibold text-red-600 mb-8 flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-red-600 rounded-full animate-pulse" />
              {t("hero.impactLine")}
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mb-10">
              <button
                onClick={() => scrollTo("cta")}
                className="group flex items-center justify-center gap-2 bg-red-600 text-white font-bold text-base px-8 py-4 rounded-lg hover:bg-red-700 transition-colors duration-150 shadow-md shadow-red-600/25"
              >
                {t("hero.cta")}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={() => scrollTo("how-it-works")}
                className="flex items-center justify-center gap-2 bg-slate-100 text-slate-800 font-semibold text-base px-8 py-4 rounded-lg hover:bg-slate-200 transition-colors duration-150"
              >
                {t("hero.ctaSecondary")}
              </button>
            </div>

            <TrustBadges className="justify-start" />
          </div>

          <div
            className={`transition-opacity duration-700 delay-200 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <div className="relative">
              <div className="bg-slate-900 rounded-lg p-6 sm:p-8 shadow-md">
                <div className="bg-white rounded-lg p-5 mb-4">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                      <Shield size={20} className="text-red-600" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-slate-900">Emergency Medical Info</p>
                      <p className="text-xs text-slate-500">QR Code Scanned</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="bg-red-50 rounded-lg p-3 border border-red-100">
                      <p className="text-[10px] font-bold text-red-700 uppercase tracking-wider mb-1">Blood Group</p>
                      <p className="text-2xl font-extrabold text-red-700">B+</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-orange-50 rounded-lg p-3 border border-orange-100">
                        <p className="text-[10px] font-bold text-orange-700 uppercase tracking-wider mb-1">Allergies</p>
                        <p className="text-sm font-bold text-slate-800">Penicillin</p>
                      </div>
                      <div className="bg-teal-50 rounded-lg p-3 border border-teal-100">
                        <p className="text-[10px] font-bold text-teal-700 uppercase tracking-wider mb-1">Condition</p>
                        <p className="text-sm font-bold text-slate-800">Diabetes T2</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <button className="w-full flex items-center justify-center gap-2 bg-red-600 text-white font-bold text-sm py-3 rounded-lg">
                    <Phone size={16} />
                    Call 112 - National Emergency
                  </button>
                  <div className="grid grid-cols-2 gap-2">
                    <button className="flex items-center justify-center gap-1.5 bg-white/10 text-white font-semibold text-xs py-2.5 rounded-lg border border-white/20">
                      <Phone size={14} />
                      108 Ambulance
                    </button>
                    <button className="flex items-center justify-center gap-1.5 bg-white/10 text-white font-semibold text-xs py-2.5 rounded-lg border border-white/20">
                      <MapPin size={14} />
                      Share Location
                    </button>
                  </div>
                </div>
              </div>

              <div className="absolute -top-4 -right-4 bg-emerald-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-md animate-bounce">
                SCAN ME
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 mt-6">
              {[
                { value: t("hero.stat1Value"), label: t("hero.stat1Label") },
                { value: t("hero.stat2Value"), label: t("hero.stat2Label") },
                { value: `Rs.${t("hero.stat3Value")}`, label: t("hero.stat3Label") },
              ].map((stat, i) => (
                <div key={i} className="text-center">
                  <p className="text-xl sm:text-2xl font-extrabold text-slate-900">{stat.value}</p>
                  <p className="text-[11px] text-slate-500 font-medium leading-tight mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSectionNew;
